import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  FeatureFlagDto,
  FeaturePermissionResponse,
  getFeatures,
} from "../components/featureManagement/sagas";

export interface FeatureActionFlags {
  /** Whether the feature itself is enabled. */
  isEnabled: boolean;
  /** True when the user has Write permission on the feature. */
  canWrite: boolean;
  /** True when the user is restricted to Read only. */
  isReadOnly: boolean;
  canAdd: boolean;
  canUpdate: boolean;
  canSave: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canImport: boolean;
}

interface FeaturePermissionContextValue {
  features: Record<string, FeatureFlagDto>;
  permission: string;
  isAdmin: boolean;
  loaded: boolean;
  refresh: () => void;
}

const READ_ONLY_FLAGS: FeatureActionFlags = {
  isEnabled: false,
  canWrite: false,
  isReadOnly: true,
  canAdd: false,
  canUpdate: false,
  canSave: false,
  canEdit: false,
  canDelete: false,
  canImport: false,
};

const FeaturePermissionContext = createContext<FeaturePermissionContextValue>({
  features: {},
  permission: "Read",
  isAdmin: false,
  loaded: false,
  refresh: () => undefined,
});

export const FeaturePermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useIsAuthenticated();
  const [features, setFeatures] = useState<Record<string, FeatureFlagDto>>({});
  const [permission, setPermission] = useState<string>("Read");
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);

  const load = () => {
    getFeatures()
      .then((response: FeaturePermissionResponse) => {
        const map: Record<string, FeatureFlagDto> = {};
        (response.Features || []).forEach((f) => {
          map[f.FeatureName] = f;
        });
        setFeatures(map);
        setPermission(response.Permission || "Read");
        setIsAdmin(Boolean(response.IsAdmin));
      })
      .catch(() => {
        // On failure default everyone to read-only.
        setFeatures({});
        setPermission("Read");
        setIsAdmin(false);
      })
      .finally(() => setLoaded(true));
  };

  useEffect(() => {
    if (isAuthenticated) {
      load();
    }
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({ features, permission, isAdmin, loaded, refresh: load }),
    [features, permission, isAdmin, loaded]
  );

  return (
    <FeaturePermissionContext.Provider value={value}>
      {children}
    </FeaturePermissionContext.Provider>
  );
};

/**
 * Returns the action flags for a given feature. Users get Write when they are a feature
 * admin (FeatureAdminUserEmails) or were granted Write on the feature in Permission
 * Management; everyone else gets read-only flags so Add/Update/Save/Edit/Delete/Import
 * actions are hidden.
 */
export const useFeaturePermission = (featureName: string): FeatureActionFlags => {
  const ctx = useContext(FeaturePermissionContext);
  const feature = ctx.features[featureName];

  if (!feature) {
    return READ_ONLY_FLAGS;
  }

  const canWrite = feature.Permission === "Write";
  return {
    isEnabled: feature.IsEnabled,
    canWrite,
    isReadOnly: !canWrite,
    canAdd: feature.CanAdd ?? canWrite,
    canUpdate: feature.CanUpdate ?? canWrite,
    canSave: feature.CanSave ?? canWrite,
    canEdit: feature.CanEdit ?? canWrite,
    canDelete: feature.CanDelete ?? canWrite,
    canImport: feature.CanImport ?? canWrite,
  };
};

export const useFeaturePermissionContext = () => useContext(FeaturePermissionContext);
